/**
 * Column sizes for the Export to Xero line table.
 *
 * Every column has exactly one width, whatever the window: when the row is wider than the window
 * the table scrolls sideways instead of narrowing a column. Fixed columns are given in px.
 * Project, Description and Discount are as wide as their longest value on the invoice, up to the
 * width given here, and cut a longer value off with "…". Dropdown columns are `max-content`, and
 * each one's heading holds a hidden sizer capped at its maximum, so the column is as wide as its
 * longest option up to that cap.
 */
export const LINE_FIXED_WIDTH_PX = {
	id: 64,
	project: 120,
	// Without the ID column the project badge sits in the Project cell, beside the name.
	projectWithBadge: 150,
	description: 150,
	quantity: 64,
	unitPrice: 72,
	discount: 64,
	tax: 56,
	total: 84,
} as const;

export const LINE_DROPDOWN_MAX_PX = {item: 240, account: 240, tracking: 180} as const;

const px = (width: number): string => `${width}px`;
const upTo = (width: number): string => `fit-content(${width}px)`;

/** The one grid template the heading row and every line share, so their columns line up. */
export const lineGridTemplate = ({showIdColumn, trackingCount}: {showIdColumn: boolean; trackingCount: number}): string =>
	[
		...(showIdColumn
			? [px(LINE_FIXED_WIDTH_PX.id), upTo(LINE_FIXED_WIDTH_PX.project)]
			: [upTo(LINE_FIXED_WIDTH_PX.projectWithBadge)]),
		'max-content',
		upTo(LINE_FIXED_WIDTH_PX.description),
		'max-content',
		px(LINE_FIXED_WIDTH_PX.quantity),
		px(LINE_FIXED_WIDTH_PX.unitPrice),
		upTo(LINE_FIXED_WIDTH_PX.discount),
		px(LINE_FIXED_WIDTH_PX.tax),
		...Array<string>(trackingCount).fill('max-content'),
		px(LINE_FIXED_WIDTH_PX.total),
	].join(' ');

/** How many option labels a column's hidden sizer measures. */
export const SIZER_LABEL_LIMIT = 20;

/**
 * The labels a column's width is measured from: all of them when there are few, else only the
 * longest. An organisation can import thousands of items, and measuring each would render a hidden
 * field per item on every pick. The longest by characters are the widest in practice; one that
 * is narrower than its character count suggests can only make the column slightly too narrow, and
 * the field cuts that value off with "…" and a tooltip.
 */
export const widestLabels = (labels: string[], limit = SIZER_LABEL_LIMIT): string[] =>
	labels.length <= limit ? labels : [...labels].sort((a, b) => b.length - a.length).slice(0, limit);
